import React, { useCallback, useEffect, useState } from 'react'
import Parse from 'parse'
import BoundedListComponent from '../Shared/BoundedListComponent'
import { formatDateTime } from '../Shared/detailFormat'
import KindSummaryComponent from './KindSummaryComponent'
import LocationWorklistComponent from './LocationWorklistComponent'
import {
  formatCount,
  formatPercent,
  lengthLabel,
  share,
  unnamedCount
} from './swimLocationStats'

const WORKLISTS = [
  {
    key: 'unnamedPools',
    label: 'Unnamed pools',
    showName: false,
    total: stats => unnamedCount(stats.pools),
    emptyText: 'Every published pool has a name.'
  },
  {
    key: 'unnamedOpenWater',
    label: 'Unnamed open water',
    showName: false,
    total: stats => unnamedCount(stats.openWater),
    emptyText: 'Every published open water spot has a name.'
  },
  {
    key: 'addressNamed',
    label: 'Named from an address',
    showName: true,
    total: stats => stats.pools.naming.address +
      stats.openWater.naming.address,
    emptyText: 'No published location is named from an address.'
  }
]

// Health of the pool and open water directory: how much there is and how
// much of it is named and enriched. Replaces the old workout heatmap.
const SwimLocationsComponent = () => {
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [worklist, setWorklist] = useState(WORKLISTS[0].key)

  const loadStats = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      setStats(await Parse.Cloud.run('getSwimLocationStats'))
    } catch (err) {
      console.error('Error loading swim location stats:', err)
      setError(err.message || String(err))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  const handleRefresh = () => {
    loadStats()
  }

  const header = (
    <div className="d-flex align-items-center mb-3">
      <h3 className="mb-0">Swim Locations</h3>
      {stats && (
        <span className="text-muted ms-3">
          Counted {formatDateTime(stats.generatedAt)} · geocoder
          v{stats.geocodeVersion} · public floor{' '}
          {stats.publicMinUniqueUsers} swimmers
        </span>
      )}
      <button
        type="button"
        className="btn btn-outline-primary btn-sm ms-auto"
        onClick={handleRefresh}
        disabled={isLoading}
      >
        {isLoading ? 'Counting…' : 'Refresh'}
      </button>
    </div>
  )

  if (error) {
    return (
      <div className="container-fluid my-3">
        {header}
        <div className="alert alert-danger">{error}</div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="container-fluid my-3">
        {header}
        <div className="text-muted">Loading…</div>
      </div>
    )
  }

  const now = new Date(stats.generatedAt)
  const activeWorklist = WORKLISTS.find(w => w.key === worklist)
  const poolLengthTotal = stats.poolLengths
    .reduce((sum, row) => sum + row.count, 0)

  return (
    <div className="container-fluid my-3">
      {header}

      <div className="row g-3 mb-3">
        <div className="col-lg-6">
          <KindSummaryComponent
            title="Pools"
            kind={stats.pools}
            isOpenWater={false}
            now={now}
          />
        </div>
        <div className="col-lg-6">
          <KindSummaryComponent
            title="Open water"
            kind={stats.openWater}
            isOpenWater
            now={now}
          />
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-lg-4">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="mb-0">Published pools by length</h5>
            </div>
            <div className="card-body">
              <table className="table table-sm mb-0">
                <tbody>
                  {stats.poolLengths.map(row => (
                    <tr key={String(row.lengthInMeters)}>
                      <td>{lengthLabel(row.lengthInMeters)}</td>
                      <td className="text-end">{formatCount(row.count)}</td>
                      <td className="text-end text-muted">
                        {formatPercent(share(row.count, poolLengthTotal))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-lg-8">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="mb-0">Published locations by country</h5>
            </div>
            <div className="card-body">
              <BoundedListComponent
                items={stats.countries}
                limit={15}
                noun="countries"
              >
                {visible => (
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Country</th>
                        <th className="text-end">Pools</th>
                        <th className="text-end">Named</th>
                        <th className="text-end">Open water</th>
                        <th className="text-end">Named</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map(row => (
                        <tr key={row.countryCode || 'none'}>
                          <td>
                            {row.countryCode || (
                              <span className="text-muted">No place</span>
                            )}
                          </td>
                          <td className="text-end">
                            {formatCount(row.pools)}
                          </td>
                          <td className="text-end text-muted">
                            {formatPercent(share(row.namedPools, row.pools))}
                          </td>
                          <td className="text-end">
                            {formatCount(row.openWater)}
                          </td>
                          <td className="text-end text-muted">
                            {formatPercent(
                              share(row.namedOpenWater, row.openWater)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </BoundedListComponent>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs">
            {WORKLISTS.map(list => (
              <li className="nav-item" key={list.key}>
                <button
                  type="button"
                  className={'nav-link' +
                    (list.key === worklist ? ' active' : '')}
                  onClick={() => setWorklist(list.key)}
                >
                  {list.label}
                  <span className="badge text-bg-light ms-2">
                    {formatCount(list.total(stats))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body">
          <p className="text-muted small">
            Published locations, most swimmers first (top 50).
          </p>
          <LocationWorklistComponent
            rows={stats[activeWorklist.key]}
            showName={activeWorklist.showName}
            emptyText={activeWorklist.emptyText}
          />
        </div>
      </div>
    </div>
  )
}

export default SwimLocationsComponent
